import React from "react";
import { Button, Typography } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CloseIcon from "@mui/icons-material/Close";
import { createPortal } from "react-dom";

export interface GuidedTutorialStep {
  title: string;
  description: string;
  targetRef?: React.RefObject<HTMLElement | null>;
}

interface GuidedTutorialOverlayProps {
  open: boolean;
  steps: GuidedTutorialStep[];
  activeStep: number;
  onBack: () => void;
  onNext: () => void;
  onClose: () => void;
}

const CARD_WIDTH = 560;
const CARD_MARGIN = 24;

const GuidedTutorialOverlay: React.FC<GuidedTutorialOverlayProps> = ({
  open,
  steps,
  activeStep,
  onBack,
  onNext,
  onClose,
}) => {
  const [targetRect, setTargetRect] = React.useState<DOMRect | null>(null);
  const step = steps[activeStep];

  React.useEffect(() => {
    if (!open) return;

    const updateTargetRect = () => {
      const rect = step?.targetRef?.current?.getBoundingClientRect();
      setTargetRect(rect || null);
    };

    step?.targetRef?.current?.scrollIntoView({
      block: "center",
      inline: "nearest",
    });
    updateTargetRect();
    const timeoutId = window.setTimeout(updateTargetRect, 120);
    window.addEventListener("resize", updateTargetRect);
    window.addEventListener("scroll", updateTargetRect, true);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener("resize", updateTargetRect);
      window.removeEventListener("scroll", updateTargetRect, true);
    };
  }, [open, activeStep, step?.targetRef]);

  if (!open || !step) return null;

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const cardWidth = Math.min(CARD_WIDTH, viewportWidth - CARD_MARGIN * 2);
  const highlightPadding = 10;
  const highlight = targetRect
    ? {
        top: Math.max(CARD_MARGIN, targetRect.top - highlightPadding),
        left: Math.max(CARD_MARGIN, targetRect.left - highlightPadding),
        width: Math.min(
          viewportWidth - CARD_MARGIN * 2,
          targetRect.width + highlightPadding * 2,
        ),
        height: targetRect.height + highlightPadding * 2,
      }
    : null;

  const cardLeft = highlight
    ? Math.min(
        Math.max(CARD_MARGIN, highlight.left + highlight.width / 2 - cardWidth / 2),
        viewportWidth - cardWidth - CARD_MARGIN,
      )
    : (viewportWidth - cardWidth) / 2;

  const cardTop = highlight
    ? highlight.top + highlight.height + 18 + 250 < viewportHeight
      ? highlight.top + highlight.height + 18
      : Math.max(CARD_MARGIN, highlight.top - 270)
    : Math.max(CARD_MARGIN, viewportHeight / 2 - 140);

  return createPortal(
    <div className="fixed inset-0 z-[1500] pointer-events-auto">
      {highlight ? (
        <div
          className="fixed rounded-lg border-2 border-blue-500 pointer-events-none"
          style={{
            top: highlight.top,
            left: highlight.left,
            width: highlight.width,
            height: highlight.height,
            boxShadow: "0 0 0 9999px rgba(2, 6, 23, 0.76)",
          }}
        />
      ) : (
        <div className="fixed inset-0 bg-slate-950/75" />
      )}

      <div
        className="fixed rounded-xl bg-slate-900 text-white shadow-2xl"
        style={{
          top: cardTop,
          left: cardLeft,
          width: cardWidth,
        }}
      >
        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <Typography variant="body2" className="font-bold text-blue-400">
              Step {activeStep + 1} of {steps.length}
            </Typography>
            <Button
              onClick={onClose}
              size="small"
              color="inherit"
              sx={{ minWidth: 0, p: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </Button>
          </div>
          <Typography variant="h6" className="font-bold mt-5">
            {step.title}
          </Typography>
          <Typography variant="body1" className="text-slate-200 mt-3">
            {step.description}
          </Typography>
          <div className="mt-5 flex items-center justify-between gap-3">
            <Button
              color="inherit"
              onClick={onClose}
              sx={{ fontWeight: 700, color: "rgb(226 232 240)" }}
            >
              Skip
            </Button>
            <div className="flex gap-2">
              <Button
                color="inherit"
                onClick={onBack}
                disabled={activeStep === 0}
                startIcon={<KeyboardArrowLeftIcon />}
                sx={{ color: "rgb(226 232 240)" }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={onNext}
                endIcon={<KeyboardArrowRightIcon />}
                sx={{ borderRadius: 2, fontWeight: 700 }}
              >
                {activeStep === steps.length - 1 ? "Finish" : "Next"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default GuidedTutorialOverlay;
